import { useEffect, useMemo, useState } from 'react';
import { Car } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import NastaveniModulu, { NastaveniTlacitko } from '../components/layout/NastaveniModulu';
import { ProvozniNaklady } from '../components/nastaveni/Ciselniky';

// ⚙ Rozvoz → Pravidla rozvozu (dřív Nastavení → Provozní náklady)
const KODY_ROZVOZU = ['SLIBTOL', 'SLIBTOL_CAS', 'STOPWAIT', 'OKLATEMAX', 'SMS_BLIZKO_METRU'];
import { REZERVA_LISTY } from '../components/MobilniLista';
import useFlotilaLive from '../hooks/useFlotilaLive';
import RozvozZive from '../components/rozvoz/RozvozZive';
import RozvozZony from '../components/rozvoz/RozvozZony';
import RozvozRidici from '../components/rozvoz/RozvozRidici';
import RozvozAuta from '../components/rozvoz/RozvozAuta';
import { SECTION_COLOR, SECTION_BG, stylKarty, stylPole, nacti } from '../components/rozvoz/ui';
import '../../css/flotila.css';

/**
 * Modul Rozvoz (dříve Flotila, přejmenováno 2026-10-10).
 *
 * Záložky (klíče přístupů `rozvoz.*`):
 *  - live   → Živě: mapa aut, rozvozů na cestě a zón (přiznaná výjimka normy — pracovní plocha)
 *  - zony   → Zóny: mnohoúhelníky v mapě s cenou, slibem, náročností a dojezdem
 *  - ridici → Řidiči: seznam se skóre, karta řidiče jako detail
 *  - auta   → Auta: seznam aut, detail s tankováním, servisem, pojištěním a nastavením
 * Seznamové záložky jedou na `ModulLayout` + `mobilRezim`, na mobilu připnuté
 * k výřezu (norma §1b). Záložka a vybraná položka jdou z adresy
 * (`#/rozvoz?tab=auta&vozidloId=3`) — odkazy z notifikací a staré `flotila`.
 */
export default function Rozvoz({ user, initialTab = null, params = {} }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile] = useState(() => window.innerWidth < 768);

    const [tab, setTab] = useState(initialTab);
    const [nastaveni, setNastaveni] = useState(false);
    useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
    const [otevritAuto, setOtevritAuto] = useState(params?.vozidloId ? Number(params.vozidloId) : null);
    const otevritRidice = params?.zamestnanecId ? Number(params.zamestnanecId) : null;

    const { vozidla, refetch } = useFlotilaLive();

    // Řidiči aut (zamestnanec_id → jméno, foto). Klíč přes sadu přiřazených
    // řidičů — vozidla se mění s každým GPS fixem, řidiči skoro nikdy.
    const [ridiciMap, setRidiciMap] = useState(new Map());
    const ridicIdsKey = useMemo(() => [...new Set(vozidla.map((v) => v.aktualni_ridic_id).filter(Boolean))].sort((a, b) => a - b).join(','), [vozidla]);
    useEffect(() => {
        if (!ridicIdsKey) return;
        let zruseno = false;
        nacti('/api/flotila/ridici').then((data) => {
            if (zruseno || !Array.isArray(data)) return;
            setRidiciMap(new Map(data.map((r) => [r.zamestnanec_id, r])));
        }).catch(() => {});
        return () => { zruseno = true; };
    }, [ridicIdsKey]);

    // Zóny pro vrstvu na Živě (editor si je načítá sám)
    const [zony, setZony] = useState([]);
    useEffect(() => {
        if (tab !== 'live') return;
        nacti('/api/rozvoz/zony').then((d) => setZony((d?.zony || []).filter((z) => z.aktivni))).catch(() => setZony([]));
    }, [tab]);

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = useMemo(() => stylKarty(isDark), [isDark]);
    const inputStyle = useMemo(() => stylPole(isDark), [isDark]);
    const pripnuta = isMobile
        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
        : { height: '100%', minHeight: 0 };
    const spolecne = { isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole: isMobile ? REZERVA_LISTY : null };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: tab === 'live' ? 'visible' : 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <Car size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Rozvoz</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                Rozvoz živě, zóny, řidiči a auta · {vozidla.length} {vozidla.length === 1 ? 'auto' : vozidla.length < 5 ? 'auta' : 'aut'}
                            </p>
                        </div>
                        {muze(user, 'rozvoz.zony', 'upravuje') && (
                            <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} popis="Nastavení rozvozu" />
                        )}
                    </div>
                )}
                <TabBar modul="rozvoz" user={user} aktivni={tab} onZmena={setTab} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
                {nastaveni && (
                    <NastaveniModulu nadpis="Nastavení rozvozu" barva={SECTION_COLOR} isDark={isDark} onClose={() => setNastaveni(false)}
                        sekce={[{ id: 'pravidla', nazev: 'Pravidla rozvozu', popis: 'sliby, časovka, čas u zákazníka', obsah: () => <ProvozniNaklady kody={KODY_ROZVOZU} isDark={isDark} barva={SECTION_COLOR}
                            popis="Podle těchto pravidel Rozvoz počítá sliby zákazníkům, časovky a čas zastávky." /> }]} />
                )}
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                {tab === 'live' && (
                    <RozvozZive vozidla={vozidla} ridiciMap={ridiciMap} zony={zony} cardStyle={cardStyle} isDark={isDark}
                        onOtevritAuto={muze(user, 'rozvoz.auta') ? (id) => { setOtevritAuto(id); setTab('auta'); } : null} />
                )}
                {tab === 'zony' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <RozvozZony {...spolecne} upravuje={muze(user, 'rozvoz.zony', 'upravuje')} />
                    </div>
                )}
                {tab === 'ridici' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <RozvozRidici {...spolecne} otevritId={otevritRidice} />
                    </div>
                )}
                {tab === 'auta' && (
                    <div className="flex flex-col" style={pripnuta}>
                        <RozvozAuta {...spolecne} vozidla={vozidla} refetch={refetch} ridiciMap={ridiciMap} otevritId={otevritAuto} />
                    </div>
                )}
            </div>
        </div>
    );
}
